import { ref } from 'vue';

// The time duration you have to press and click for the action to be registered, in ms.
const LONG_PRESS_DURATION_MS = 500;

// Shared with map dragging so small finger movements do not start a pan during a long press.
export const LONG_PRESS_MOVE_TOLERANCE_PIXEL = 10;

export interface UseMapContextMenuOptions {
  viewport: HTMLElement;
  onOpen: (event: MouseEvent) => void;
}

export interface UseMapContextMenu {
  setup: () => void;
  cleanup: () => void;
}

export function useMapContextMenu(options: UseMapContextMenuOptions): UseMapContextMenu {
  const longPressTimer = ref<ReturnType<typeof setTimeout> | null>(null);
  let touchStartPosition: { x: number; y: number } | null = null;
  let ignoreOpeningClick = false;

  const clearLongPress = (): void => {
    touchStartPosition = null;
    if (longPressTimer.value !== null) {
      clearTimeout(longPressTimer.value);
      longPressTimer.value = null;
    }
  };

  const handleContextMenu = (event: MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    options.onOpen(event);
  };

  const handleTouchStart = (event: TouchEvent): void => {
    ignoreOpeningClick = false;
    clearLongPress();
    if (event.touches.length !== 1) {
      return;
    }

    const touch = event.touches[0];
    touchStartPosition = { x: touch.clientX, y: touch.clientY };

    longPressTimer.value = setTimeout(() => {
      ignoreOpeningClick = true;
      // Simulate a mouse event at the touch position
      const mouseEvent = new MouseEvent('contextmenu', {
        clientX: touch.clientX,
        clientY: touch.clientY,
        bubbles: true,
        cancelable: true,
      });
      handleContextMenu(mouseEvent);
    }, LONG_PRESS_DURATION_MS);
  };

  const allowNextClick = (): void => {
    ignoreOpeningClick = false;
  };

  /** Consumes the click some browsers send when the opening long press ends. */
  const handleClick = (event: MouseEvent): void => {
    if (!ignoreOpeningClick) {
      return;
    }

    ignoreOpeningClick = false;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  /**
   * Allows small finger movements while long pressing on mobile to open the menu.
   */
  const handleTouchMove = (event: TouchEvent): void => {
    if (event.touches.length !== 1) {
      clearLongPress();
      return;
    }

    if (!touchStartPosition) {
      return;
    }

    const touch = event.touches[0];
    const distanceFromTouchStartPixels = Math.hypot(
      touch.clientX - touchStartPosition.x,
      touch.clientY - touchStartPosition.y,
    );

    if (distanceFromTouchStartPixels > LONG_PRESS_MOVE_TOLERANCE_PIXEL) {
      clearLongPress();
    }
  };

  const setup = (): void => {
    const { viewport } = options;
    viewport.addEventListener('contextmenu', handleContextMenu);
    viewport.addEventListener('pointerdown', allowNextClick);
    viewport.addEventListener('click', handleClick, true);
    viewport.addEventListener('touchstart', handleTouchStart);
    viewport.addEventListener('touchend', clearLongPress);
    viewport.addEventListener('touchmove', handleTouchMove);
    viewport.addEventListener('touchcancel', clearLongPress);
  };

  const cleanup = (): void => {
    const { viewport } = options;
    viewport.removeEventListener('contextmenu', handleContextMenu);
    viewport.removeEventListener('pointerdown', allowNextClick);
    viewport.removeEventListener('click', handleClick, true);
    viewport.removeEventListener('touchstart', handleTouchStart);
    viewport.removeEventListener('touchend', clearLongPress);
    viewport.removeEventListener('touchmove', handleTouchMove);
    viewport.removeEventListener('touchcancel', clearLongPress);
    clearLongPress();
    ignoreOpeningClick = false;
  };

  return {
    setup,
    cleanup,
  };
}
